import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { ErrorBoundary } from 'bernie-view';

import { PropertySearchListingViewModel } from 'models/hotel/search-types';
import { ResultType } from 'constants/resultType';

import { queue } from 'uitk-react-images';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkSecondaryButton } from 'uitk-react-button';
import { UitkSpacing } from 'uitk-react-spacing';

import { MesoBrandResultListing } from 'components/meso/meso-brand-result-listing';
import { MesoInlineAdComponent, shouldMesoInlineAdRender } from 'components/meso/meso-inline-ad';
import { Result } from './result';

const LOADING_LIST = new Array(6).fill({ model: {} });
const LAZY_LOADING_IMAGE_INDEX = 3;
const RL_POS = 11;

const ButtonTracked = ClickTracker(UitkSecondaryButton);

export interface PropertySearchListingsProps {
  setResultRef: (ref: HTMLElement, index: number) => void;
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  triggeredPageName?: string;
  propertySearchListings: PropertySearchListingViewModel[];
  searchCriteria: any;
  hotelsMaxDisplay: number;
  isLoading: boolean;
}

const getNumNonProperties = (propertySearchListings: PropertySearchListingViewModel[]) =>
  propertySearchListings.filter(listing => listing.resultType !== ResultType.HOTEL).length;

const getNumListingsToShow = (propertySearchListings: PropertySearchListingViewModel[], hotelsMaxDisplay: number) =>
  hotelsMaxDisplay + getNumNonProperties(propertySearchListings);

const getTruncatedListings = (propertySearchListings: PropertySearchListingViewModel[], hotelsMaxDisplay: number) => {
  if (propertySearchListings) {
    return propertySearchListings.slice(0, getNumListingsToShow(propertySearchListings, hotelsMaxDisplay));
  }

  return [];
};

export const PropertySearchListings = inject(
  'searchResults',
  'analytics',
  'routeStore',
  'advertising'
)(
  observer(({ searchResults, analytics, routeStore, advertising, setResultRef, showMoreLabel, handleShowMore, fetchOnScrollResolved }) => {
    const allHotelsDisplayed = searchResults.hotelsMaxDisplay >= searchResults.properties?.length;
    const loadMoreRfrr = `:LoadMore${searchResults.getPageLoadNumber()}`;

    return (
      <>
        <SearchListings
          analytics={analytics}
          routeStore={routeStore}
          triggeredPageName={advertising.triggeredPageName}
          setResultRef={setResultRef}
          propertySearchListings={searchResults.propertySearchListings}
          searchCriteria={searchResults.searchCriteria}
          hotelsMaxDisplay={searchResults.hotelsMaxDisplay}
          isLoading={searchResults.isLoading}
        />
        <ShowMoreButton
          allHotelsDisplayed={allHotelsDisplayed}
          onClickHandler={handleShowMore}
          rfrr={loadMoreRfrr}
          linkName="Show more results"
          isLoading={searchResults.isLoading}
          text={showMoreLabel}
          searchResults={searchResults}
          fetchOnScrollResolved={fetchOnScrollResolved}
        />
      </>
    );
  })
);

// Wrapping SearchListings because functions wrapped in `observer` are treated as class components and it breaks React hooks.

const SearchListings: React.FunctionComponent<PropertySearchListingsProps> = ({
  analytics,
  routeStore,
  setResultRef,
  triggeredPageName,
  propertySearchListings,
  searchCriteria,
  hotelsMaxDisplay,
  isLoading,
}) => {
  React.useEffect(() => () => queue.dequeueAll('HSR'));
  const { endDate, startDate } = searchCriteria;
  const showLoadingList = isLoading && (!propertySearchListings || propertySearchListings.length === 0);
  const classNames = `listing`;

  const truncatedPropertySearchListings: PropertySearchListingViewModel[] = showLoadingList
    ? LOADING_LIST
    : getTruncatedListings(propertySearchListings, hotelsMaxDisplay);
  let propertyIndex = -1;

  return (
    <section className="results" data-stid="section-results">
      <ol className="results-list no-bullet">
        {truncatedPropertySearchListings.map((listingViewModel, index) => {
          propertyIndex += listingViewModel.resultType === ResultType.HOTEL ? 1 : 0;

          return (
            <React.Fragment key={`resultListing-${index}`}>
              {propertyIndex === RL_POS && (
                <UitkSpacing padding={{ blockstart: 'three' }}>
                  <MesoBrandResultListing uci="RL1" errorBoundaryLabel="hotel.search.meso" />
                </UitkSpacing>
              )}
              {shouldMesoInlineAdRender(triggeredPageName, propertyIndex) && (
                <MesoInlineAdComponent
                  key={`mesoInlineAd${propertyIndex}`}
                  slotIndex={propertyIndex}
                  classNames={classNames}
                  pageId={triggeredPageName}
                />
              )}

              <UitkSpacing margin={{ blockstart: 'three' }}>
                <Result
                  lazyLoadImg={index >= LAZY_LOADING_IMAGE_INDEX}
                  loading={showLoadingList}
                  className={classNames}
                  data-stid={listingViewModel.dataStid}
                  {...listingViewModel}
                  {...{ startDate, endDate, index, setResultRef, routeStore, analytics }}
                />
              </UitkSpacing>
            </React.Fragment>
          );
        })}
      </ol>
    </section>
  );
};

const ShowMoreButton = ({ allHotelsDisplayed, onClickHandler, isLoading, searchResults, fetchOnScrollResolved, ...rest }) => {
  const { isReducedPayloadExperimentV1 } = searchResults;
  const btnDisabledState = isReducedPayloadExperimentV1 ? false : isLoading;

  if (
    (isReducedPayloadExperimentV1 && allHotelsDisplayed && fetchOnScrollResolved) ||
    (!isReducedPayloadExperimentV1 && allHotelsDisplayed)
  ) {
    return null;
  }

  return (
    <ErrorBoundary label="hotel.search.showMoreResultsButton">
      <UitkSpacing padding={{ blockstart: 'three' }}>
        <UitkLayoutFlex justifyContent="center" tag="section">
          <ButtonTracked disabled={btnDisabledState} data-stid="show-more-results" onClick={onClickHandler} {...rest} />
        </UitkLayoutFlex>
      </UitkSpacing>
    </ErrorBoundary>
  );
};
